'use client';

import { useEffect, useState } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from '@/lib/toast';
import { handleApiError } from '@/lib/error-handler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  listEmergencyBypass,
  enableEmergencyBypass,
  disableEmergencyBypass,
  type BypassEntry,
} from '@/services/api/iam-admin';

export default function EmergencyBypassPage() {
  const { t } = useTranslation();
  const [list, setList] = useState<BypassEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      setList(await listEmergencyBypass());
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.loadFailed });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const onDisable = async (endpoint: string) => {
    if (!confirm(t.iamAdmin.emergencyBypass.confirmDisable.replace('{endpoint}', endpoint))) return;
    try {
      await disableEmergencyBypass(endpoint);
      toast.success(t.iamAdmin.common.deleted);
      reload();
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.deleteFailed });
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t.iamAdmin.emergencyBypass.title}
          </h1>
          <p className="text-sm text-gray-500">{t.iamAdmin.emergencyBypass.subtitle}</p>
        </div>
        <Button variant="destructive" onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          {t.iamAdmin.emergencyBypass.newButton}
        </Button>
      </div>

      <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start">
        <AlertTriangle className="w-5 h-5 mr-2 mt-0.5 text-amber-600 shrink-0" />
        <div className="text-sm text-amber-800">{t.iamAdmin.emergencyBypass.warning}</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.iamAdmin.emergencyBypass.columns.endpoint}</TableHead>
              <TableHead>{t.iamAdmin.emergencyBypass.columns.reason}</TableHead>
              <TableHead>{t.iamAdmin.emergencyBypass.columns.enabledAt}</TableHead>
              <TableHead>{t.iamAdmin.emergencyBypass.columns.expiresAt}</TableHead>
              <TableHead className="text-right">{t.iamAdmin.common.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">{t.iamAdmin.common.loading}</TableCell></TableRow>}
            {!loading && list.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">{t.iamAdmin.emergencyBypass.empty}</TableCell></TableRow>}
            {list.map((b) => (
              <TableRow key={b.endpoint}>
                <TableCell className="font-mono text-sm">{b.endpoint}</TableCell>
                <TableCell>{b.reason}</TableCell>
                <TableCell className="text-sm text-gray-600">{new Date(b.enabledAt).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-red-600">{new Date(b.expiresAt).toLocaleString()}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => onDisable(b.endpoint)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {createOpen && (
        <EnableDialog onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); reload(); }} />
      )}
    </div>
  );
}

function EnableDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [endpoint, setEndpoint] = useState('');
  const [reason, setReason] = useState('');
  const [ttlMin, setTtlMin] = useState(60);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!endpoint.trim() || !reason.trim() || ttlMin < 1) {
      toast.error(t.iamAdmin.common.fillRequired);
      return;
    }
    if (ttlMin > 240) {
      toast.error(t.iamAdmin.emergencyBypass.ttlMaxError);
      return;
    }
    setSubmitting(true);
    try {
      await enableEmergencyBypass({
        endpoint: endpoint.trim(),
        reason: reason.trim(),
        ttlSec: ttlMin * 60,
      });
      toast.success(t.iamAdmin.common.saved);
      onSaved();
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.saveFailed });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.iamAdmin.emergencyBypass.newButton}</DialogTitle>
          <DialogDescription>{t.iamAdmin.emergencyBypass.dialogDesc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t.iamAdmin.emergencyBypass.columns.endpoint}</Label>
            <Input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="POST /api/v1/users" />
            <p className="mt-1 text-xs text-gray-500">{t.iamAdmin.emergencyBypass.endpointHint}</p>
          </div>
          <div>
            <Label>{t.iamAdmin.emergencyBypass.columns.reason}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="incident-2026-04-25 / 工单号" />
          </div>
          <div>
            <Label>{t.iamAdmin.emergencyBypass.ttlLabel}</Label>
            <Input type="number" min={1} max={240} value={ttlMin} onChange={(e) => setTtlMin(parseInt(e.target.value || '0', 10))} />
            <p className="mt-1 text-xs text-gray-500">{t.iamAdmin.emergencyBypass.ttlHint}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.iamAdmin.common.cancel}</Button>
          <Button variant="destructive" onClick={submit} disabled={submitting}>
            {submitting ? t.iamAdmin.common.submitting : t.iamAdmin.emergencyBypass.confirmEnable}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
